import {
  AI_COPY_FILTERS,
  MAX_AI_COPY_SELECTION,
  type AiCopyFilter,
  type AiCopyItem,
  type AiCopyQueuedResponse,
  type AiCopySummary,
  type ApproveAiCopyRequest,
  type RegenerateAiCopyRequest,
  type UpdateAiCopySettingsRequest,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  buttonClass,
  Card,
  CategoryBrowser,
  CheckboxField,
  ConfirmDialog,
  countText,
  fill,
  FlyerPreview,
  hasGroupedProducts,
  isApiError,
  LoadMore,
  Loading,
  PageHeader,
  setValues,
  TextField,
  Thumbnail,
  toggleValue,
  returnToPrevious,
  useListFilters,
} from '@avrahami/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState, type SyntheticEvent } from 'react';
import {
  keys,
  platformApi,
  tenantCopyGroupsQuery,
  tenantCopyItemsQuery,
  tenantCopySummaryQuery,
  tenantFlyerQuery,
  tenantQuery,
  type AiCopyFilters,
} from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { CopyGroupEdit } from '../components/CopyGroupEdit';
import { CopyStateBadge, ProductStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import { formatCount } from '../lib/ai';
import {
  AI_COPY_POLL_MS,
  approvableIds,
  approvalText,
  canApprove,
  COPY_FIELDS,
  copyImageUrl,
  copyProgress,
  copyRowState,
  copyWorkOpen,
  emptyText,
  filterCount,
  hasOwnCopy,
  isLongText,
  jobErrorText,
  jobOpen,
  queuedText,
  regenerateAllText,
  type CopyField,
} from '../lib/ai-copy';
import { errorMessage } from '../lib/format';

const LINK_CLASS = 'font-medium text-brand-700 hover:underline';
const BOX_CLASS = 'size-4 rounded border-slate-300 accent-brand-600';

/** The list opens on the copy nobody approved or edited yet, unless a link says otherwise. */
const DEFAULT_FILTER: AiCopyFilter = 'GENERATED';

/**
 * "Marketing copy" of one store: the copy the content engine wrote for the store's catalog
 * products, to approve, edit or ask for again. The platform staff manage it (adr/0023); the
 * store's own admin has no such screen. Facts are never shown for editing here. The copy of a
 * whole group (category, manufacturer) is edited at once, and every product opens its own page
 * (adr/0043).
 *
 * The products are shown under the store's categories: first the categories, a category opens
 * to its sub-categories, and only a sub-category shows products. A search shows its results at
 * once. What is ticked is kept across categories and searches.
 */
export function TenantCopyPage() {
  const { tenantId } = useParams({ from: '/app/tenants/$tenantId/copy' });
  const { filter = DEFAULT_FILTER, q = '' } = useSearch({ from: '/app/tenants/$tenantId/copy' });
  const session = useQuery(sessionQuery);
  const canManage = session.data !== undefined && hasCapability(session.data, 'platform.ai.manage');
  // Only for the store's name in the title: the screen works without it.
  const tenant = useQuery(tenantQuery(tenantId));
  const summary = useQuery({ ...tenantCopySummaryQuery(tenantId), enabled: canManage });
  const name = tenant.data?.tenant.displayName;
  return (
    <>
      <Link
        to="/tenants/$tenantId"
        params={{ tenantId }}
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.aiCopy.back}
      </Link>
      <PageHeader title={name === undefined ? he.aiCopy.title : `${he.aiCopy.title}: ${name}`} />
      {canManage ? (
        <>
          {summary.isPending ? <Loading /> : null}
          {summary.isError ? (
            <Alert>
              {errorMessage(summary.error, {
                FORBIDDEN: he.aiCopy.forbidden,
                NOT_FOUND: he.aiCopy.notFound,
              })}
            </Alert>
          ) : null}
          {/* A failed re-read keeps the screen (and what is being edited) on the last answer. */}
          {summary.data === undefined ? null : (
            <TenantCopy
              key={`${tenantId}|${filter}|${q}`}
              tenantId={tenantId}
              summary={summary.data}
              initial={{ filter, q }}
            />
          )}
        </>
      ) : (
        <Alert>{he.aiCopy.forbidden}</Alert>
      )}
    </>
  );
}

interface Notice {
  tone: 'success' | 'error';
  text: string;
}

function TenantCopy({
  tenantId,
  summary,
  initial,
}: {
  tenantId: string;
  summary: AiCopySummary;
  initial: AiCopyFilters;
}) {
  const queryClient = useQueryClient();
  const {
    draft: { q: search },
    filters,
    change,
    apply,
    applyNow,
  } = useListFilters(`copy:${tenantId}|${initial.filter}|${initial.q}`, initial);
  // Kept across searches: one can search, tick, search again and approve everything ticked.
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [confirmingRewriteAll, setConfirmingRewriteAll] = useState(false);
  const [rewriting, setRewriting] = useState<AiCopyItem | null>(null);
  const [confirmingStoreSeo, setConfirmingStoreSeo] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const working = copyWorkOpen(summary);
  const searching = filters.q.trim() !== '';
  const groups = useQuery({
    ...tenantCopyGroupsQuery(tenantId, filters.filter),
    enabled: !searching,
    // The categories follow the writing together with the summary, and rest when it ends.
    refetchInterval: working ? AI_COPY_POLL_MS : false,
  });

  // The store's card shows the same counts, and every action here is logged.
  const refresh = async () => {
    await Promise.all(
      [keys.tenantCopy(tenantId), keys.tenantAi(tenantId), keys.audit(tenantId)].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  };

  // The writing ended: the last rows changed after the final poll, and so did the store's card.
  const wasWorking = useRef(working);
  useEffect(() => {
    const ended = wasWorking.current && !working;
    wasWorking.current = working;
    if (!ended) return;
    for (const queryKey of [keys.tenantCopy(tenantId), keys.tenantAi(tenantId)]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [working, queryClient, tenantId]);

  const approve = useMutation({
    mutationFn: (body: ApproveAiCopyRequest) => platformApi.approveAiCopy(tenantId, body),
    onSuccess: async (result) => {
      setConfirmingAll(false);
      setSelected([]);
      setNotice({ tone: 'success', text: approvalText(result) });
      await refresh();
    },
    onError: async (error) => {
      setConfirmingAll(false);
      // Some of the ticked products no longer wait for approval: show the list as it is now.
      const moved = isApiError(error, 'BAD_REQUEST') || isApiError(error, 'CONFLICT');
      setNotice({
        tone: 'error',
        text: errorMessage(error, {
          BAD_REQUEST: he.aiCopy.selectionChanged,
          CONFLICT: he.aiCopy.selectionChanged,
        }),
      });
      if (!moved) return;
      setSelected([]);
      await refresh();
    },
  });

  // Requests that only queue writing: nothing is assumed, the summary is read again and followed.
  const queueing = {
    onSuccess: async (result: AiCopyQueuedResponse) => {
      setRewriting(null);
      setConfirmingRewriteAll(false);
      setNotice({ tone: 'success', text: queuedText(result.queued) });
      await refresh();
    },
    onError: async (error: unknown) => {
      setRewriting(null);
      setConfirmingRewriteAll(false);
      setNotice({
        tone: 'error',
        text: errorMessage(error, { CONFLICT: he.aiCopy.alreadyWriting }),
      });
      if (isApiError(error, 'CONFLICT')) await refresh();
    },
  };
  const regenerate = useMutation({
    mutationFn: (body: RegenerateAiCopyRequest) => platformApi.regenerateAiCopy(tenantId, body),
    ...queueing,
  });
  const retryFailed = useMutation({
    mutationFn: () => platformApi.retryFailedAiCopy(tenantId),
    ...queueing,
  });
  const generateMissing = useMutation({
    mutationFn: () => platformApi.generateMissingAiCopy(tenantId),
    ...queueing,
  });
  const storeSeo = useMutation({
    mutationFn: () => platformApi.regenerateStoreSeo(tenantId),
    onSuccess: async () => {
      setConfirmingStoreSeo(false);
      setNotice({ tone: 'success', text: he.aiCopy.storeSeo.queued });
      await refresh();
    },
    onError: async (error: unknown) => {
      setConfirmingStoreSeo(false);
      setNotice({
        tone: 'error',
        text: errorMessage(error, { CONFLICT: he.aiCopy.alreadyWriting }),
      });
      if (isApiError(error, 'CONFLICT')) await refresh();
    },
  });

  const settings = useMutation({
    mutationFn: (body: UpdateAiCopySettingsRequest) =>
      platformApi.updateAiCopySettings(tenantId, body),
    onSuccess: async (updated, body) => {
      queryClient.setQueryData(keys.tenantCopySummary(tenantId), updated);
      if (body.stopped === undefined) {
        setNotice({
          tone: 'success',
          text: updated.requiresApproval ? he.aiCopy.approvalOn : he.aiCopy.approvalOff,
        });
        return;
      }
      setNotice({
        tone: 'success',
        text: updated.stopped ? he.aiCopy.stoppedNow : he.aiCopy.resumedNow,
      });
      // The store's card and its log show the change too.
      await refresh();
    },
    onError: (error) => {
      setNotice({ tone: 'error', text: errorMessage(error) });
    },
  });

  const acting =
    approve.isPending ||
    regenerate.isPending ||
    retryFailed.isPending ||
    generateMissing.isPending ||
    storeSeo.isPending ||
    settings.isPending;
  const selectable = filters.filter === 'GENERATED';
  const chosen = new Set(selected);
  const overLimit = selected.length > MAX_AI_COPY_SELECTION;

  const onSearch = (event: SyntheticEvent) => {
    event.preventDefault();
    apply();
  };
  const showFilter = (filter: AiCopyFilter) => {
    setSelected([]);
    setNotice(null);
    applyNow({ filter });
  };
  const rewrite = (item: AiCopyItem) => {
    setNotice(null);
    regenerate.mutate({ selection: 'SELECTED', productIds: [item.productId] });
  };
  /** The products of one category, or (without a category) the results of the search. */
  const copyItems = (categoryId: string) => (
    <CopyItems
      tenantId={tenantId}
      filters={filters}
      categoryId={categoryId}
      working={working}
      selectable={selectable}
      chosen={chosen}
      acting={acting}
      onToggle={(productId) => {
        setSelected((current) => toggleValue(current, productId));
      }}
      onSet={(productIds, on) => {
        setSelected((current) => setValues(current, productIds, on));
      }}
      onApprove={(item) => {
        setNotice(null);
        approve.mutate({ selection: 'SELECTED', productIds: [item.productId] });
      }}
      onRewrite={(item) => {
        // Asking first only when there is a text to lose.
        if (hasOwnCopy(item)) setRewriting(item);
        else rewrite(item);
      }}
    />
  );

  return (
    <div className="flex flex-col gap-6">
      <Card title={he.aiCopy.summary}>
        <div className="flex flex-col gap-4" data-testid="copy-summary">
          <p className="text-sm text-slate-600">{he.aiCopy.intro}</p>
          <Progress summary={summary} working={working} />
          {summary.paused ? (
            <div data-testid="copy-paused">
              <Alert tone="warning">{he.aiCopy.paused}</Alert>
            </div>
          ) : null}
          {summary.stopped ? (
            <div data-testid="copy-stopped">
              <Alert tone="warning">{he.aiCopy.stopped}</Alert>
            </div>
          ) : null}
          <div>
            <Button
              tone={summary.stopped ? 'primary' : 'secondary'}
              data-testid={summary.stopped ? 'copy-resume' : 'copy-stop'}
              busy={settings.isPending && settings.variables.stopped !== undefined}
              disabled={acting}
              onClick={() => {
                setNotice(null);
                settings.mutate({ stopped: !summary.stopped });
              }}
            >
              {summary.stopped ? he.aiCopy.resume : he.aiCopy.stop}
            </Button>
          </div>
          <CheckboxField
            role="switch"
            label={he.aiCopy.approvalSwitch}
            hint={he.aiCopy.approvalHint}
            name="requiresApproval"
            data-testid="copy-requires-approval"
            // While the change is on its way the switch shows what was asked for.
            checked={
              settings.isPending
                ? (settings.variables.requiresApproval ?? summary.requiresApproval)
                : summary.requiresApproval
            }
            disabled={acting}
            onChange={(event) => {
              setNotice(null);
              settings.mutate({ requiresApproval: event.target.checked });
            }}
          />
        </div>
      </Card>

      <Card title={he.aiCopy.storeSeo.title}>
        <div className="flex flex-col gap-3" data-testid="copy-store-seo">
          <p className="text-sm text-slate-600">{he.aiCopy.storeSeo.intro}</p>
          <dl className="flex flex-col gap-2 text-sm">
            {(
              [
                [he.aiCopy.storeSeo.seoTitle, summary.storeSeo.title, 'copy-store-seo-title'],
                [
                  he.aiCopy.storeSeo.seoDescription,
                  summary.storeSeo.description,
                  'copy-store-seo-description',
                ],
              ] as const
            ).map(([label, value, testId]) => (
              <div key={testId}>
                <dt className="text-xs font-medium text-slate-500">{label}</dt>
                <dd
                  className={value === null ? 'text-slate-400' : 'text-slate-900'}
                  data-testid={testId}
                >
                  {value ?? he.aiCopy.noText}
                </dd>
              </div>
            ))}
          </dl>
          {jobOpen(summary.storeSeo) ? (
            <p className="text-xs text-slate-500" data-testid="copy-store-seo-writing">
              {he.aiCopy.storeSeo.writing}
            </p>
          ) : null}
          {jobErrorText(summary.storeSeo) === null ? null : (
            <p className="text-xs text-red-600" data-testid="copy-store-seo-failed">
              {jobErrorText(summary.storeSeo)}
            </p>
          )}
          <div>
            <Button
              tone="secondary"
              data-testid="copy-store-seo-regenerate"
              busy={storeSeo.isPending}
              disabled={acting || jobOpen(summary.storeSeo)}
              onClick={() => {
                setNotice(null);
                // Asking first only when there is a text to lose.
                if (summary.storeSeo.title === null && summary.storeSeo.description === null) {
                  storeSeo.mutate();
                } else setConfirmingStoreSeo(true);
              }}
            >
              {he.aiCopy.regenerate}
            </Button>
          </div>
        </div>
      </Card>

      <CopyGroupEdit tenantId={tenantId} disabled={acting} />

      <Card title={he.aiCopy.list}>
        <div className="flex flex-col gap-4">
          <div role="group" aria-label={he.aiCopy.filters} className="flex flex-wrap gap-2">
            {AI_COPY_FILTERS.map((filter) => (
              <FilterChip
                key={filter}
                filter={filter}
                count={filterCount(summary, filter)}
                active={filters.filter === filter}
                onChoose={showFilter}
              />
            ))}
          </div>

          <form onSubmit={onSearch} className="flex flex-wrap items-end gap-3">
            <TextField
              label={he.aiCopy.search}
              name="q"
              type="search"
              className="w-full sm:w-72"
              maxLength={100}
              data-testid="copy-search"
              value={search}
              onChange={(event) => {
                change({ q: event.target.value });
              }}
            />
            <Button type="submit" tone="secondary">
              {he.common.search}
            </Button>
          </form>

          {notice === null ? null : (
            <div className="sticky top-2 z-10" data-testid="copy-notice" data-tone={notice.tone}>
              <Alert tone={notice.tone}>{notice.text}</Alert>
            </div>
          )}

          <div className="flex flex-col gap-3" data-testid="copy-bulk">
            {overLimit ? (
              <p className="text-xs text-amber-700" data-testid="copy-over-limit">
                {fill(he.aiCopy.overLimit, { max: MAX_AI_COPY_SELECTION })}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              {selectable ? (
                <>
                  <Button
                    data-testid="copy-approve-selected"
                    disabled={acting || selected.length === 0 || overLimit}
                    onClick={() => {
                      setNotice(null);
                      approve.mutate({ selection: 'SELECTED', productIds: [...selected] });
                    }}
                  >
                    {fill(he.aiCopy.approveSelected, { count: selected.length })}
                  </Button>
                  <Button
                    tone="secondary"
                    data-testid="copy-approve-all"
                    disabled={acting || summary.products.generated === 0}
                    onClick={() => {
                      setNotice(null);
                      setConfirmingAll(true);
                    }}
                  >
                    {fill(he.aiCopy.approveAll, {
                      count: formatCount(summary.products.generated),
                    })}
                  </Button>
                  <Button
                    tone="secondary"
                    data-testid="copy-regenerate-all"
                    disabled={acting || summary.products.generated === 0}
                    onClick={() => {
                      setNotice(null);
                      setConfirmingRewriteAll(true);
                    }}
                  >
                    {he.aiCopy.regenerateAll}
                  </Button>
                  {selected.length > 0 ? (
                    <Button
                      tone="secondary"
                      data-testid="copy-clear-selection"
                      onClick={() => {
                        setSelected([]);
                      }}
                    >
                      {he.aiCopy.clearSelection}
                    </Button>
                  ) : null}
                </>
              ) : null}
              {filters.filter === 'FAILED' ? (
                <Button
                  data-testid="copy-retry-failed"
                  busy={retryFailed.isPending}
                  disabled={acting || summary.jobs.failed === 0}
                  onClick={() => {
                    setNotice(null);
                    retryFailed.mutate();
                  }}
                >
                  {he.aiCopy.retryFailed}
                </Button>
              ) : null}
              {filters.filter === 'WITHOUT_COPY' ? (
                <Button
                  data-testid="copy-generate-missing"
                  busy={generateMissing.isPending}
                  disabled={acting || summary.products.unqueued === 0}
                  onClick={() => {
                    setNotice(null);
                    generateMissing.mutate();
                  }}
                >
                  {he.aiCopy.generateMissing}
                </Button>
              ) : null}
            </div>
          </div>

          {searching ? (
            copyItems('')
          ) : (
            <>
              {groups.isPending ? <Loading /> : null}
              {groups.isError ? <Alert>{errorMessage(groups.error)}</Alert> : null}
              {groups.data === undefined ? null : hasGroupedProducts(groups.data) ? (
                <CategoryBrowser groups={groups.data} renderProducts={copyItems} />
              ) : (
                <p className="text-sm text-slate-500" data-testid="copy-empty">
                  {emptyText(filters.filter, false)}
                </p>
              )}
            </>
          )}
        </div>
      </Card>

      <ConfirmDialog
        open={confirmingAll}
        title={he.aiCopy.approveAllTitle}
        confirmLabel={he.aiCopy.approve}
        confirmTestId="copy-confirm-approve-all"
        busy={approve.isPending}
        onConfirm={() => {
          approve.mutate({ selection: 'ALL' });
        }}
        onClose={() => {
          setConfirmingAll(false);
        }}
      >
        {countText(summary.products.generated, {
          one: he.aiCopy.approveAllOne,
          many: he.aiCopy.approveAllMany,
        })}
      </ConfirmDialog>
      <ConfirmDialog
        open={rewriting !== null}
        title={he.aiCopy.regenerateTitle}
        confirmLabel={he.aiCopy.regenerate}
        confirmTestId="copy-confirm-regenerate"
        busy={regenerate.isPending}
        onConfirm={() => {
          if (rewriting !== null) rewrite(rewriting);
        }}
        onClose={() => {
          setRewriting(null);
        }}
      >
        {rewriting === null ? null : fill(he.aiCopy.regenerateBody, { name: rewriting.name })}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmingStoreSeo}
        title={he.aiCopy.storeSeo.regenerateTitle}
        confirmLabel={he.aiCopy.regenerate}
        confirmTestId="copy-confirm-store-seo"
        busy={storeSeo.isPending}
        onConfirm={() => {
          storeSeo.mutate();
        }}
        onClose={() => {
          setConfirmingStoreSeo(false);
        }}
      >
        {he.aiCopy.storeSeo.regenerateBody}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmingRewriteAll}
        title={he.aiCopy.regenerateAllTitle}
        confirmLabel={he.aiCopy.regenerate}
        confirmTestId="copy-confirm-regenerate-all"
        busy={regenerate.isPending}
        onConfirm={() => {
          setNotice(null);
          regenerate.mutate({ selection: 'ALL_GENERATED' });
        }}
        onClose={() => {
          setConfirmingRewriteAll(false);
        }}
      >
        <span data-testid="copy-regenerate-all-body">
          {regenerateAllText(summary.products.generated)}
        </span>
      </ConfirmDialog>
    </div>
  );
}

/**
 * The products of one category in one state of their copy, or (without a category) the results
 * of a search: the rows, a box that ticks every row loaded, and "load more".
 */
function CopyItems({
  tenantId,
  filters,
  categoryId,
  working,
  selectable,
  chosen,
  acting,
  onToggle,
  onSet,
  onApprove,
  onRewrite,
}: {
  tenantId: string;
  filters: AiCopyFilters;
  categoryId: string;
  working: boolean;
  selectable: boolean;
  chosen: ReadonlySet<string>;
  acting: boolean;
  onToggle: (productId: string) => void;
  onSet: (productIds: readonly string[], on: boolean) => void;
  onApprove: (item: AiCopyItem) => void;
  onRewrite: (item: AiCopyItem) => void;
}) {
  const list = useInfiniteQuery({
    ...tenantCopyItemsQuery(tenantId, filters, categoryId),
    // The rows follow the writing together with the summary, and rest when it ends.
    refetchInterval: working ? AI_COPY_POLL_MS : false,
  });
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const loadedIds = approvableIds(items);
  return (
    <div className="flex flex-col gap-3">
      {list.isPending ? <Loading /> : null}
      {list.isError ? <Alert>{errorMessage(list.error)}</Alert> : null}
      {list.isSuccess && items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="copy-empty">
          {emptyText(filters.filter, filters.q.trim() !== '')}
        </p>
      ) : null}
      {selectable && loadedIds.length > 0 ? (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="copy-select-all"
            checked={loadedIds.every((id) => chosen.has(id))}
            onChange={(event) => {
              onSet(loadedIds, event.target.checked);
            }}
            className={BOX_CLASS}
          />
          {he.aiCopy.selectAll}
        </label>
      ) : null}
      {items.length > 0 ? (
        <ul className="flex flex-col gap-3" data-testid="copy-list">
          {items.map((item) => (
            <CopyRow
              key={item.productId}
              tenantId={tenantId}
              item={item}
              selectable={selectable}
              chosen={chosen.has(item.productId)}
              acting={acting}
              onToggle={() => {
                onToggle(item.productId);
              }}
              onApprove={() => {
                onApprove(item);
              }}
              onRewrite={() => {
                onRewrite(item);
              }}
            />
          ))}
        </ul>
      ) : null}
      <LoadMore
        hasMore={list.hasNextPage}
        busy={list.isFetchingNextPage}
        onMore={() => {
          void list.fetchNextPage();
        }}
      />
    </div>
  );
}

/** How much of the catalog has copy; a bar while the engine is writing. */
function Progress({ summary, working }: { summary: AiCopySummary; working: boolean }) {
  const { ready, total, percent } = copyProgress(summary);
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-slate-900" data-testid="copy-progress">
        {fill(he.aiCopy.progress, { ready: formatCount(ready), total: formatCount(total) })}
      </p>
      {working ? (
        <>
          <div
            role="progressbar"
            aria-label={he.aiCopy.progressLabel}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            data-testid="copy-progress-bar"
            className="h-2 overflow-hidden rounded-full bg-slate-200"
          >
            <div className="h-full bg-brand-600" style={{ width: `${String(percent)}%` }} />
          </div>
          <p className="text-xs text-slate-500" data-testid="copy-working">
            {he.aiCopy.working}
          </p>
        </>
      ) : null}
    </div>
  );
}

/** One state of the copy, as a toggle with its count. */
function FilterChip({
  filter,
  count,
  active,
  onChoose,
}: {
  filter: AiCopyFilter;
  count: number;
  active: boolean;
  onChoose: (filter: AiCopyFilter) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      data-testid="copy-filter"
      data-filter={filter}
      onClick={() => {
        onChoose(filter);
      }}
      className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-brand-600 text-white'
          : 'bg-white text-slate-800 ring-1 ring-slate-300 hover:bg-slate-100'
      }`}
    >
      {he.aiCopy.filter[filter]} <span data-testid="copy-filter-count">({formatCount(count)})</span>
    </button>
  );
}

/** One product: what it is, the state of its copy, the copy itself, and what can be done. */
function CopyRow({
  tenantId,
  item,
  selectable,
  chosen,
  acting,
  onToggle,
  onApprove,
  onRewrite,
}: {
  tenantId: string;
  item: AiCopyItem;
  selectable: boolean;
  chosen: boolean;
  acting: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onRewrite: () => void;
}) {
  const [showFlyer, setShowFlyer] = useState(false);
  const state = copyRowState(item);
  const jobError = jobErrorText(item);
  const approvable = canApprove(item);
  const hasText = COPY_FIELDS.some((field) => item[field] !== null);
  return (
    <li
      data-testid="copy-row"
      data-sku={item.sku}
      data-state={state}
      className="flex flex-col gap-3 rounded-md p-3 ring-1 ring-slate-200"
    >
      <div className="flex items-start gap-3">
        {selectable && approvable ? (
          <input
            type="checkbox"
            aria-label={`${he.aiCopy.select}: ${item.name}`}
            data-testid="copy-select"
            checked={chosen}
            onChange={onToggle}
            className={`mt-1 ${BOX_CLASS}`}
          />
        ) : null}
        {item.image === null ? (
          <span className="w-14 shrink-0 text-xs text-slate-500">{he.common.noImage}</span>
        ) : (
          <span className="shrink-0">
            <Thumbnail src={copyImageUrl(tenantId, item.image)} alt="" size={56} />
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 className="text-sm font-semibold break-words text-slate-900">
            <Link
              to="/tenants/$tenantId/products/$productId"
              params={{ tenantId, productId: item.productId }}
              className={LINK_CLASS}
              data-testid="copy-product-link"
            >
              {item.name}
            </Link>
          </h3>
          <p className="text-xs text-slate-500">
            {he.aiCopy.sku}: <span className="ltr">{item.sku}</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <ProductStatusBadge status={item.status} hiddenFromShoppers={item.hiddenFromShoppers} />
            <CopyStateBadge state={state} />
          </div>
        </div>
      </div>

      {jobError === null ? null : (
        <div data-testid="copy-job-error">
          <Alert>{jobError}</Alert>
        </div>
      )}
      {item.publishBlockers.length === 0 ? null : (
        <p className="text-xs text-slate-600" data-testid="copy-blockers">
          {he.aiCopy.blockers}{' '}
          {item.publishBlockers.map((blocker) => he.publishBlockers[blocker]).join(', ')}
        </p>
      )}

      <>
        {hasText ? (
          <dl className="flex flex-col gap-2 text-sm" data-testid="copy-text">
            {COPY_FIELDS.map((field) => (
              <CopyText key={field} field={field} text={item[field]} />
            ))}
          </dl>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {approvable ? (
            <Button
              aria-label={`${he.aiCopy.approve}: ${item.name}`}
              data-testid="copy-approve"
              disabled={acting}
              onClick={onApprove}
            >
              {he.aiCopy.approve}
            </Button>
          ) : null}
          {/* The product page edits the copy with everything else of the product (adr/0055). */}
          <Link
            to="/tenants/$tenantId/products/$productId"
            params={{ tenantId, productId: item.productId }}
            aria-label={`${he.aiCopy.edit}: ${item.name}`}
            data-testid="copy-edit"
            className={buttonClass('secondary')}
          >
            {he.aiCopy.edit}
          </Link>
          <Button
            tone="secondary"
            aria-label={`${he.aiCopy.regenerate}: ${item.name}`}
            data-testid="copy-regenerate"
            // An open job already writes this product's copy.
            disabled={acting || jobOpen(item)}
            onClick={onRewrite}
          >
            {he.aiCopy.regenerate}
          </Button>
          {hasOwnCopy(item) ? (
            <Button
              tone="secondary"
              aria-expanded={showFlyer}
              aria-label={`${showFlyer ? he.aiCopy.flyers.hide : he.aiCopy.flyers.show}: ${item.name}`}
              data-testid="copy-flyer-toggle"
              onClick={() => {
                setShowFlyer((open) => !open);
              }}
            >
              {showFlyer ? he.aiCopy.flyers.hide : he.aiCopy.flyers.show}
            </Button>
          ) : null}
        </div>
        {showFlyer ? <CopyFlyer tenantId={tenantId} productId={item.productId} /> : null}
      </>
    </li>
  );
}

/** One product's flyer as the store gets it (docs/FLYERS.md): its template's, with a download. */
function CopyFlyer({ tenantId, productId }: { tenantId: string; productId: string }) {
  const flyer = useQuery(tenantFlyerQuery(tenantId, productId));
  const words = he.aiCopy.flyers;
  return (
    <div className="flex flex-col gap-3 rounded-md bg-slate-50 p-3" data-testid="copy-flyer">
      {flyer.isPending ? <Loading /> : null}
      {flyer.isError ? <Alert>{errorMessage(flyer.error)}</Alert> : null}
      {flyer.data === undefined ? null : (
        <>
          <FlyerPreview
            flyer={flyer.data}
            mediaUrl={(file) => copyImageUrl(tenantId, file)}
            words={words}
          />
          {flyer.data.price === null ? (
            <p className="text-xs text-slate-500">{words.noPrice}</p>
          ) : null}
        </>
      )}
    </div>
  );
}

/** One field of the copy as it reads now; a long description is cut, with "show more". */
function CopyText({ field, text }: { field: CopyField; text: string | null }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const long = text !== null && isLongText(text);
  return (
    <div className="flex flex-col gap-0.5" data-field={field}>
      <dt className="text-xs text-slate-500">{he.aiCopy.fields[field]}</dt>
      <dd className="break-words text-slate-800">
        {text === null ? (
          <span className="text-slate-500">{he.aiCopy.noText}</span>
        ) : (
          <>
            <p id={id} className={`whitespace-pre-line ${long && !open ? 'line-clamp-3' : ''}`}>
              {text}
            </p>
            {long ? (
              <button
                type="button"
                aria-expanded={open}
                aria-controls={id}
                data-testid="copy-show-more"
                className={`text-xs ${LINK_CLASS}`}
                onClick={() => {
                  setOpen((current) => !current);
                }}
              >
                {open ? he.aiCopy.showLess : he.aiCopy.showMore}
              </button>
            ) : null}
          </>
        )}
      </dd>
    </div>
  );
}
